import { useState, useEffect, useCallback, useMemo } from 'react';
import useSWR from 'swr';
import { Team, TeamResult, TeamRequest, TeamRequestResult } from 'models/CommonType';

const BASE_URL = `${process.env.REACT_APP_DOMAIN}/api`;

// Helper function to build query string
const buildQueryString = (params: Record<string, any>): string => {
  const searchParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value));
    }
  });
  
  return searchParams.toString();
};

// API fetcher function
const teamFetcher = async (url: string, authKey: string): Promise<TeamResult> => {
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authKey}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch teams');
  }

  return response.json();
};

// Hook to fetch teams for a campaign
export const useTeams = (
  campaignId: string,
  authKey: string,
  searchText: string = '',
  pageNumber: number = 1,
  enabled: boolean = true
) => {
  const params = useMemo(() => ({
    campaign: campaignId,
    'page[number]': pageNumber,
    search: searchText,
    sort: 'rank'
  }), [campaignId, pageNumber, searchText]);

  const url = useMemo(() => {
    const queryString = buildQueryString(params);
    return `${BASE_URL}/teams?${queryString}`;
  }, [params]);
  
  const shouldFetch = enabled && campaignId && authKey;
  
  const swrKey = useMemo(() => 
    shouldFetch ? [url, authKey] : null,
    [shouldFetch, url, authKey]
  );
  
  const { data, error, isLoading, mutate } = useSWR(
    swrKey,
    ([url, authKey]) => teamFetcher(url, authKey),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
      revalidateOnMount: true,
    }
  );

  return {
    data: data?.results || [],
    meta: data?.meta,
    totalCount: data?.meta?.pagination?.total || 0,
    hasNextPage: data?.meta?.pagination ? 
      data.meta.pagination.page < data.meta.pagination.pages : false,
    isError: error,
    isLoading,
    mutate,
  };
};

// Hook for paginated teams with search
export const useTeamsPaginated = (
  campaignId: string,
  authKey: string,
  enabled: boolean = true
) => {
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [allTeams, setAllTeams] = useState<Team[]>([]);

  const { data, totalCount, hasNextPage, isLoading, mutate } = useTeams(
    campaignId,
    authKey,
    searchText,
    currentPage,
    enabled
  );

  // Update teams when data changes
  useEffect(() => {
    if (data && data.length > 0) {
      if (currentPage === 1) {
        setAllTeams(data);
      } else {
        setAllTeams(prev => [...prev, ...data]);
      }
    } else if (currentPage === 1 && data) {
      setAllTeams([]);
    }
  }, [data, currentPage]);

  const loadNextPage = useCallback(() => {
    if (hasNextPage && !isLoading) {
      setCurrentPage(prev => prev + 1);
    }
  }, [hasNextPage, isLoading]);

  const handleSearch = useCallback((text: string) => {
    setSearchText(text);
    setCurrentPage(1);
    setAllTeams([]);
  }, []);

  const refreshTeams = useCallback(() => {
    setCurrentPage(1);
    setAllTeams([]);
    mutate();
  }, [mutate]);

  return {
    teams: allTeams,
    totalCount,
    hasNextPage,
    isLoading,
    searchText,
    setSearchText: handleSearch,
    loadNextPage,
    refreshTeams,
  };
};

// Fetch individual team details API
export const fetchTeamDetails = async (
  teamId: string,
  authKey: string
): Promise<Team> => {
  const response = await fetch(`${BASE_URL}/teams/${teamId}/`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authKey}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to fetch team details');
  }

  return response.json();
};

// Hook to fetch team details
export const useTeamDetails = (teamId: string, authKey: string, enabled: boolean = true) => {
  const { data, error, isLoading, mutate } = useSWR(
    enabled && teamId && authKey ? [`/teams/${teamId}`, authKey] : null,
    ([_, authKey]) => fetchTeamDetails(teamId, authKey),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  return {
    team: data,
    isLoading,
    isError: error,
    refreshTeam: mutate,
  };
};

// Helper function to get team member names
export const getTeamMemberNames = (members: Team['members']): string => {
  if (!members || members.length === 0) return '';
  
  let memberNames = '';
  for (let i = 0; i < members.length; i++) {
    const user = members[i];
    let separator = i === 0 ? '' : ', ';
    
    if (i === members.length - 1 && members.length > 1) {
      separator = ' and ';
    }
    
    memberNames += separator + `${user.first_name} ${user.last_name}`;
  }
  
  return memberNames;
};

// Team creation response interface
export interface TeamCreationResponse {
  id: string;
  created: string;
  campaign: string;
  name: string;
  user: string;
  members: string[];
  description?: string;
}

// Team creation API
export const createTeam = async (
  teamName: string,
  campaignUrl: string,
  userUrl: string,
  authKey: string
): Promise<TeamCreationResponse> => {
  const payload = {
    campaign: campaignUrl,
    user: userUrl,
    name: teamName,
    members: [userUrl]
  };

  const response = await fetch(`${BASE_URL}/teams/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authKey}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to create team');
  }

  return response.json();
};

// Helper function to format miles text
export const formatMilesText = (miles: number): string => {
  return miles === 1 ? `${miles} mile` : `${miles} miles`;
};

// Team update API
export const updateTeamName = async (
  teamId: string,
  name: string,
  authKey: string
): Promise<any> => {
  const response = await fetch(`${BASE_URL}/teams/${teamId}/`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authKey}`,
    },
    body: JSON.stringify({ name }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to update team name');
  }

  return response.json();
};

// Team description update API
export const updateTeamDescription = async (
  teamId: string,
  description: string,
  authKey: string
): Promise<any> => {
  const response = await fetch(`${BASE_URL}/teams/${teamId}/`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authKey}`,
    },
    body: JSON.stringify({ description }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to update team description');
  }

  return response.json();
};

// Join team with code API
export const joinTeamWithCode = async (
  teamId: string,
  code: string,
  authKey: string
): Promise<any> => {
  // Use team-specific endpoint with iOS payload structure
  const payload = {
    data: {
      attributes: {
        code: code
      }
    }
  };

  const response = await fetch(`${BASE_URL}/teams/${teamId}/join/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authKey}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to join team');
  }

  return response.json();
};

// Request to join team API
export const requestToJoinTeam = async (
  teamId: string,
  authKey: string
): Promise<any> => {
  // Construct team URL following the same pattern as user URL
  const teamUrl = `${process.env.REACT_APP_DOMAIN}/api/teams/${teamId}/`;
  
  const response = await fetch(`${BASE_URL}/team-requests/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authKey}`,
    },
    body: JSON.stringify({ team: teamUrl }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to send join request');
  }

  return response.json();
};

// Leave team API
export const leaveTeam = async (
  teamId: string,
  authKey: string
): Promise<any> => {
  const response = await fetch(`${BASE_URL}/teams/${teamId}/quit/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authKey}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to leave team');
  }

  return response.json();
};

// Upload team photo API
export const uploadTeamPhoto = async (
  teamId: string,
  file: File,
  authKey: string
): Promise<any> => {
  const formData = new FormData();
  // Use the same field name as working user avatar upload
  formData.append('file', file);

  const response = await fetch(`${BASE_URL}/teams/${teamId.toLowerCase()}/update_image/`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${authKey}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to upload team photo');
  }

  return response.json();
};

// Disband team API
export const disbandTeam = async (
  teamId: string,
  authKey: string
): Promise<boolean> => {
  const response = await fetch(`${BASE_URL}/teams/${teamId.toLowerCase()}/`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authKey}`,
    },
  });

  if (response.status === 204) {
    return true;
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to disband team');
  }

  return true;
};

// Remove team member API
export const removeTeamMember = async (
  teamId: string,
  memberId: string,
  authKey: string
): Promise<any> => {
  // Construct user URL following the same pattern as team creation
  const userUrl = `${process.env.REACT_APP_DOMAIN}/api/users/${memberId}/`;
  
  const json = {
    data: {
      attributes: {
        user: userUrl
      }
    }
  };

  const response = await fetch(`${BASE_URL}/teams/${teamId.toLowerCase()}/kick/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authKey}`,
    },
    body: JSON.stringify(json),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to remove team member');
  }

  return response.json();
};

// Update team image API
export const updateTeamImage = async (
  teamId: string,
  imageFile: File,
  authKey: string
): Promise<any> => {
  const formData = new FormData();
  // Use the same field name as working user avatar upload
  formData.append('file', imageFile);

  const response = await fetch(`${BASE_URL}/teams/${teamId}/update_image/`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${authKey}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to update team image');
  }

  return response.json();
};

// Team request API endpoints
const TEAM_REQUEST_ENDPOINT = '/team-requests/';

// Fetch team requests for admin (paginated)
export const fetchTeamRequests = async (
  teamId: string,
  authKey: string,
  page: number = 1
): Promise<{ requests: TeamRequest[], hasNextPage: boolean }> => {
  const params = new URLSearchParams({
    team: teamId.toLowerCase(),
    'page[number]': page.toString()
  });

  const response = await fetch(`${BASE_URL}${TEAM_REQUEST_ENDPOINT}?${params}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authKey}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to fetch team requests');
  }

  const data: TeamRequestResult = await response.json();
  return {
    requests: data.results,
    hasNextPage: !!data.next
  };
};

// Accept team request
export const acceptTeamRequest = async (
  requestId: string,
  authKey: string
): Promise<boolean> => {
  const response = await fetch(`${BASE_URL}${TEAM_REQUEST_ENDPOINT}${requestId.toLowerCase()}/accept/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authKey}`,
    },
  });

  if (response.status === 204) {
    return true;
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to accept request');
  }

  return true;
};

// Reject team request
export const rejectTeamRequest = async (
  requestId: string,
  authKey: string
): Promise<boolean> => {
  const response = await fetch(`${BASE_URL}${TEAM_REQUEST_ENDPOINT}${requestId.toLowerCase()}/reject/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authKey}`,
    },
  });

  if (response.status === 204) {
    return true;
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to reject request');
  }

  return true;
};

// Hook to fetch team requests with pagination
export const useTeamRequests = (
  teamId: string,
  authKey: string,
  enabled: boolean = true
) => {
  const [allRequests, setAllRequests] = useState<TeamRequest[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const loadRequests = useCallback(async (page: number = 1) => {
    if (!enabled || !teamId) return;
    
    setIsLoading(true);
    try {
      const result = await fetchTeamRequests(teamId, authKey, page);
      
      if (page === 1) {
        setAllRequests(result.requests);
      } else {
        setAllRequests(prev => [...prev, ...result.requests]);
      }
      
      setCurrentPage(page);
      setHasNextPage(result.hasNextPage);
    } catch (error: any) {
      console.error('Error fetching team requests:', error);
    } finally {
      setIsLoading(false);
    }
  }, [teamId, authKey, enabled]);

  const loadNextPage = useCallback(() => {
    if (hasNextPage && !isLoading) {
      loadRequests(currentPage + 1);
    }
  }, [hasNextPage, isLoading, currentPage, loadRequests]);

  const refreshRequests = useCallback(() => {
    setAllRequests([]);
    setCurrentPage(1);
    setHasNextPage(false);
    loadRequests(1);
  }, [loadRequests]);

  useEffect(() => {
    if (enabled && teamId) {
      loadRequests(1);
    }
  }, [teamId, enabled, loadRequests]);

  return {
    requests: allRequests,
    isLoading,
    hasNextPage,
    loadNextPage,
    refreshRequests
  };
};